import { useToast } from '@chakra-ui/react'
import { Field, Form, Formik } from 'formik'
import React from 'react'
import { Button } from '../../../../components/Button'
import { FormLabel } from '../../../../components/FormLabel'
import { ContactSelectInput } from '../../../../components/inputs/ContactSelectInput'
import { IconPickerInput } from '../../../../components/inputs/IconPickerInput'
import { MapInput } from '../../../../components/inputs/MapInput/MapInput'
import { TextInput } from '../../../../components/inputs/TextInput'
import { PROJECT_ICONS } from '../../../../lib/projectIcons'
import { useSimpleProjectUpdateMutation } from '../../../../model/api/project/useSimpleProjectUpdateMutation'
import { ProjectEntityData } from '../../../../model/interfaces/generated'
import { CheckboxInput } from 'src/components/inputs/CheckboxInput'
import RadioInput from 'src/components/inputs/RadioInput'
import { ProjectFormCategoryVariantEnum } from 'shared'

interface Props {
  project: ProjectEntityData
}

export const ProjectSimpleForm: React.FC<Props> = (props) => {
  const toast = useToast()
  const { mutateAsync: updateProject, isLoading: isUpdatingProject } =
    useSimpleProjectUpdateMutation()
  return (
    <Formik
      initialValues={{
        name: props.project.name,
        subject: props.project.subject,
        number: props.project.number,
        investor: props.project.investor?.id,
        latitude: props.project.latitude,
        longitude: props.project.longitude,
        icon: props.project.icon,
        backgroundMaterialsAndSurveys: props.project.backgroundMaterialsAndSurveys,
        projectDocumentation: props.project.projectDocumentation,
        buildingPermit: props.project.buildingPermit,
        demolitionPermit: props.project.demolitionPermit,
        groupBuildingPermit: props.project.groupBuildingPermit,
        generalPermit: props.project.generalPermit,
        landUseChangePermit: props.project.landUseChangePermit,
        constructionExecutionPermit: props.project.constructionExecutionPermit,
        buildingPassport: props.project.buildingPassport,
        engineering: props.project.engineering,
        construction: props.project.construction,
        constructionDiary: props.project.constructionDiary,
        photodocumentation: props.project.photodocumentation,
        defectsAndIncompletes: props.project.defectsAndIncompletes,
      }}
      onSubmit={async (data) => {
        await updateProject({ ...data, id: props.project.id })
        toast({
          title: 'Uloženo',
          status: 'success',
          isClosable: true,
          position: 'top',
        })
      }}
    >
      {(formik) => (
        <Form>
          <div className="mt-3 grid grid-cols-3 gap-x-6 gap-y-3">
            <div className="col-span-3">
              <Field name="icon" component={IconPickerInput} label="Ikonka" icons={PROJECT_ICONS} />
            </div>
            <div className="sm:col-12">
              <Field name="name" component={TextInput} label="Název projektu" />
            </div>
            <div className="sm:col-12">
              <Field name="subject" component={TextInput} label="Předmět" />
            </div>
            <div className="sm:col-12">
              <Field name="number" component={TextInput} label="Číslo projektu" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3">
            <div className="sm:col-12">
              <Field
                name="investor"
                label="Investor"
                component={ContactSelectInput}
                options={[]}
                forbidRemove={true}
              />
            </div>
          </div>
          <div className="sm:col-12 pt-8">
            <h3 className="font-bold text-sm ">Rozsah</h3>
            <div className="pt-1">
              <Field
                name="backgroundMaterialsAndSurveys"
                component={CheckboxInput}
                label="Podklady a průzkumy"
              />
              <Field
                name="projectDocumentation"
                component={CheckboxInput}
                label="Projektová dokumentace"
              />
              {formik.values.projectDocumentation && (
                <div className="pl-5 py-2">
                  <div>
                    <Field
                      name="buildingPermit"
                      component={CheckboxInput}
                      label="Povolení stavby"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="demolitionPermit"
                      component={CheckboxInput}
                      label="Odstranení stavby"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="groupBuildingPermit"
                      component={CheckboxInput}
                      label="Povolení stavby v případě souboru staveb"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                      disabled={true}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="generalPermit"
                      component={CheckboxInput}
                      label="Rámcové povolení"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                      disabled={true}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="landUseChangePermit"
                      component={CheckboxInput}
                      label="Povolení změny využití území"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                      disabled={true}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="constructionExecutionPermit"
                      component={CheckboxInput}
                      label="Provádění stavby"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                      disabled={true}
                    />
                  </div>
                  <div className="pt-2">
                    <Field
                      name="buildingPassport"
                      component={CheckboxInput}
                      label="Pasport stavby"
                      falseyValue={ProjectFormCategoryVariantEnum.OFF}
                      truthyValue={ProjectFormCategoryVariantEnum.CREATE}
                      disabled={true}
                    />
                  </div>
                </div>
              )}
              <Field
                name="engineering"
                component={CheckboxInput}
                label="Inženýring"
              />
              <Field name="construction" component={CheckboxInput} label="Stavba" />
              {formik.values.construction && (
                <div className="pl-5 py-2">
                  <Field
                    name="constructionDiary"
                    component={CheckboxInput}
                    label="Stavební deník"
                  />
                  <Field
                    name="photodocumentation"
                    component={CheckboxInput}
                    label="Fotodokumentace"
                  />
                  <Field
                    name="defectsAndIncompletes"
                    component={CheckboxInput}
                    label="Vady a nedodělky"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 mb-8">
            <div className="mb-3">
              <FormLabel>Poloha</FormLabel>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-3">
                <MapInput
                  existingPointLngLat={
                    formik.values.longitude && formik.values.latitude
                      ? [formik.values.longitude, formik.values.latitude]
                      : undefined
                  }
                  onPointSelectedLngLat={(lngLat) => {
                    formik.setFieldValue('longitude', lngLat[0])
                    formik.setFieldValue('latitude', lngLat[1])
                  }}
                />
              </div>
              <div className="flex flex-col gap-3">
                <Field name="latitude" label="Latitude" component={TextInput} disabled={true} />
                <Field name="longitude" label="Longitude" component={TextInput} disabled={true} />
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-5">
            <Button type="submit" isLoading={isUpdatingProject}>
              Uložit
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
